<template>
  <div>
    <!-- Toolbar -->
    <div class="d-flex align-items-center gap-3 flex-wrap mb-3">
      <div class="input-group" style="max-width: 280px">
        <span class="input-group-text"><i class="bi bi-search" /></span>
        <input
          v-model="search"
          type="search"
          class="form-control"
          placeholder="Filter categories…"
          :disabled="loading"
        />
      </div>
      <span v-if="flat.length" class="text-muted small">
        {{ flat.length.toLocaleString() }} categories
        <template v-if="visibleIds"> · {{ visibleIds.size.toLocaleString() }} matched</template>
      </span>
      <button class="ms-auto btn btn-outline-primary" :disabled="syncing" @click="syncCategories">
        <i class="bi-arrow-counterclockwise me-1"></i>{{ syncing ? 'Syncing…' : 'Sync eBay Categories' }}
      </button>
    </div>

    <!-- Sync feedback -->
    <div v-if="syncResult" class="alert alert-success py-2 mb-3">
      Synced {{ syncResult.count.toLocaleString() }} categories ({{ syncResult.affectedRows }} rows affected).
    </div>
    <div v-if="syncError" class="alert alert-danger py-2 mb-3">{{ syncError }}</div>

    <!-- Tree -->
    <div class="cat-tree-wrap border rounded p-2">
      <loading-spinner v-if="loading" />
      <div v-else-if="loadError" class="alert alert-danger mb-0">{{ loadError }}</div>
      <template v-else-if="roots.length">
        <div v-if="visibleIds && visibleIds.size === 0" class="text-muted small p-2">
          No categories match "{{ search }}".
        </div>
        <ul v-else class="list-unstyled mb-0">
          <CategoryTreeNode
            v-for="root in roots"
            :key="root.id"
            :node="root"
            :visible-ids="visibleIds"
            :search="search"
          />
        </ul>
      </template>
      <p v-else class="text-muted small mb-0 p-2">No categories loaded yet. Click Sync to import from eBay.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import CategoryTreeNode from '@/admin/components/CategoryTreeNode.vue'

const store = useStore()

const flat = ref([])
const loading = ref(false)
const loadError = ref(null)
const search = ref('')
const syncing = ref(false)
const syncResult = ref(null)
const syncError = ref(null)

const nodeMap = computed(() => {
  const map = new Map()
  for (const node of flat.value) {
    map.set(node.id, { ...node, children: [] })
  }
  for (const node of map.values()) {
    if (node.parentId) {
      const parent = map.get(node.parentId)
      if (parent) parent.children.push(node)
    }
  }
  return map
})

const roots = computed(() => {
  const result = []
  for (const node of nodeMap.value.values()) {
    if (!node.parentId) result.push(node)
  }
  return result.sort((a, b) => a.name.localeCompare(b.name))
})

const visibleIds = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return null
  const result = new Set()
  for (const node of flat.value) {
    if (node.name.toLowerCase().includes(term)) {
      result.add(node.id)
      let parentId = node.parentId
      while (parentId) {
        result.add(parentId)
        parentId = nodeMap.value.get(parentId)?.parentId ?? 0
      }
    }
  }
  return result
})

async function loadCategories() {
  loading.value = true
  loadError.value = null
  try {
    const res = await store.state.api.get('/admin/ebay/categories')
    flat.value = res.data
  } catch (e) {
    loadError.value = e.message || 'Failed to load categories'
  } finally {
    loading.value = false
  }
}

async function syncCategories() {
  syncing.value = true
  syncResult.value = null
  syncError.value = null
  try {
    const res = await store.state.api.get('/admin/ebay/get-categories')
    syncResult.value = res.data
    await loadCategories()
  } catch (e) {
    syncError.value = e.message || 'Failed to sync categories'
  } finally {
    syncing.value = false
  }
}

onMounted(loadCategories)
</script>

<style lang="scss" scoped>
.cat-tree-wrap {
  max-height: 70vh;
  overflow-y: auto;
  background: #fafafa;
}
</style>
